/**
 * The ScopingMemo — what a read-only scoping session brings back from the code.
 *
 * ADR-0027: every proposal and every plan is grounded in the repository by a
 * read-only E2B session before a human is asked to approve it. The memo is that
 * session's only durable output, bound to a Work Item at an exact `baseSha`.
 *
 * The type is split at a TRUST BOUNDARY that matters more than it looks:
 *
 *   - {@link ScopingMemoDraft} is the part a model inside the sandbox authors.
 *     It is parsed defensively ({@link parseScopingMemoDraft}) with hard bounds
 *     on every array and string, because it lands in `runs.artifacts` JSONB and
 *     an unbounded model can otherwise write a megabyte into a hot row.
 *   - {@link ScopingMemo} adds the fields only the HOST may state: which run
 *     produced it, the base SHA it was actually pinned to, how long it took and
 *     what it cost. A model that could name its own `baseSha` could claim
 *     evidence it never read; a model that could name its own `costMillicents`
 *     could hide a budget breach.
 *
 * The memo is cached per `(workItemId, baseSha)`, so a critic revision or a
 * second proposal pass at the same head never re-spins a sandbox.
 */
/** Schema version stamped on every memo; bump when the shape changes. */
export const SCOPING_MEMO_SCHEMA_VERSION = 1;
/**
 * `RunArtifact.kind` for a persisted memo. Artifacts are appended to
 * `runs.artifacts`, so this string is the cache's lookup key — treat it as a
 * durable identifier, not a label.
 */
export const SCOPING_MEMO_ARTIFACT_KIND = "scoping_memo";
/**
 * How much of the codebase the change is likely to touch. Deliberately three
 * coarse buckets: this feeds an effort SIGNAL a human reads at a gate, not an
 * estimate anything computes with.
 */
export const SCOPING_BLAST_RADII = ["small", "medium", "large"];
/**
 * Bounds on model-authored content. Every one of these is a JSONB write into a
 * hot `runs` row, so the ceilings are deliberately low enough that a
 * pathological session cannot bloat the table.
 */
export const SCOPING_MEMO_LIMITS = {
    maxFilesExamined: 200,
    maxPathLength: 512,
    maxEvidenceLength: 2_000,
    maxListEntries: 50,
    maxListEntryLength: 1_000,
    maxFilesToChange: 200,
    maxValidatedCommands: 20,
    maxCommandLength: 1_000,
};
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
/**
 * Repository-relative, non-traversing path. An absolute path or a `..` segment
 * in a memo is either a confused model or an attempt to name a file outside the
 * clone; both make the evidence meaningless, so both are rejected.
 */
function pathProblem(value, label) {
    if (typeof value !== "string")
        return `${label} must be a string`;
    const trimmed = value.trim();
    if (!trimmed)
        return `${label} must not be empty`;
    if (trimmed.length > SCOPING_MEMO_LIMITS.maxPathLength) {
        return `${label} exceeds ${SCOPING_MEMO_LIMITS.maxPathLength} characters`;
    }
    if (trimmed.startsWith("/") || /^[a-zA-Z]:[\\/]/.test(trimmed)) {
        return `${label} must be repository-relative`;
    }
    if (trimmed.split(/[\\/]/).includes("..")) {
        return `${label} must not traverse outside the repository`;
    }
    return null;
}
function readStringList(value, label, errors, maxEntries, maxLength) {
    if (!Array.isArray(value)) {
        errors.push(`${label} must be an array of strings`);
        return [];
    }
    if (value.length > maxEntries) {
        errors.push(`${label} must contain at most ${maxEntries} entries`);
        return [];
    }
    const out = [];
    for (const [index, entry] of value.entries()) {
        if (typeof entry !== "string" || !entry.trim()) {
            errors.push(`${label}[${index}] must be a non-empty string`);
            continue;
        }
        if (entry.length > maxLength) {
            errors.push(`${label}[${index}] exceeds ${maxLength} characters`);
            continue;
        }
        out.push(entry.trim());
    }
    return out;
}
function readPathList(value, label, errors) {
    if (!Array.isArray(value)) {
        errors.push(`${label} must be an array of repository-relative paths`);
        return [];
    }
    if (value.length > SCOPING_MEMO_LIMITS.maxFilesToChange) {
        errors.push(`${label} must contain at most ${SCOPING_MEMO_LIMITS.maxFilesToChange} paths`);
        return [];
    }
    const out = [];
    for (const [index, entry] of value.entries()) {
        const problem = pathProblem(entry, `${label}[${index}]`);
        if (problem) {
            errors.push(problem);
            continue;
        }
        out.push(entry.trim());
    }
    return out;
}
function readEffortSignal(value, errors) {
    if (!isRecord(value)) {
        errors.push("effortSignal must be an object");
        return null;
    }
    const buildRan = value.buildRan;
    const testsRan = value.testsRan;
    const testsPassed = value.testsPassed ?? null;
    const blastRadius = value.blastRadius;
    if (typeof buildRan !== "boolean")
        errors.push("effortSignal.buildRan must be a boolean");
    if (typeof testsRan !== "boolean")
        errors.push("effortSignal.testsRan must be a boolean");
    if (testsPassed !== null && typeof testsPassed !== "boolean") {
        errors.push("effortSignal.testsPassed must be a boolean or null");
    }
    if (typeof blastRadius !== "string" || !SCOPING_BLAST_RADII.includes(blastRadius)) {
        errors.push(`effortSignal.blastRadius must be one of ${SCOPING_BLAST_RADII.join(", ")}`);
    }
    if (errors.length > 0)
        return null;
    // "We did not run the tests" and "we ran them and they failed" are different
    // facts; a false/false pair would erase the distinction the gate needs.
    if (testsRan === false && testsPassed !== null) {
        errors.push("effortSignal.testsPassed must be null when testsRan is false");
        return null;
    }
    if (testsRan === true && testsPassed === null) {
        errors.push("effortSignal.testsPassed must be stated when testsRan is true");
        return null;
    }
    return {
        buildRan: buildRan,
        testsRan: testsRan,
        testsPassed: testsPassed,
        blastRadius: blastRadius,
    };
}
function readValidatedCommands(value, errors) {
    if (!Array.isArray(value)) {
        errors.push("validatedCommands must be an array");
        return [];
    }
    if (value.length > SCOPING_MEMO_LIMITS.maxValidatedCommands) {
        errors.push(`validatedCommands must contain at most ${SCOPING_MEMO_LIMITS.maxValidatedCommands} entries`);
        return [];
    }
    const out = [];
    for (const [index, entry] of value.entries()) {
        if (!isRecord(entry)) {
            errors.push(`validatedCommands[${index}] must be an object`);
            continue;
        }
        const command = entry.command;
        const exitCode = entry.exitCode;
        if (typeof command !== "string" || !command.trim()) {
            errors.push(`validatedCommands[${index}].command must be a non-empty string`);
            continue;
        }
        if (command.length > SCOPING_MEMO_LIMITS.maxCommandLength) {
            errors.push(`validatedCommands[${index}].command exceeds ${SCOPING_MEMO_LIMITS.maxCommandLength} characters`);
            continue;
        }
        if (typeof exitCode !== "number" || !Number.isInteger(exitCode)) {
            errors.push(`validatedCommands[${index}].exitCode must be an integer`);
            continue;
        }
        out.push({ command: command.trim(), exitCode });
    }
    return out;
}
/**
 * Parse the model's memo JSON. Returns the errors rather than throwing: the
 * session's ONE retry hands them straight back to the model as the correction
 * prompt, so the messages are part of the output contract, not debug noise.
 */
export function parseScopingMemoDraft(value) {
    const errors = [];
    if (!isRecord(value)) {
        return { ok: false, errors: ["memo must be a JSON object"] };
    }
    const filesExamined = [];
    if (!Array.isArray(value.filesExamined)) {
        errors.push("filesExamined must be an array");
    }
    else if (value.filesExamined.length > SCOPING_MEMO_LIMITS.maxFilesExamined) {
        errors.push(`filesExamined must contain at most ${SCOPING_MEMO_LIMITS.maxFilesExamined} entries`);
    }
    else {
        for (const [index, entry] of value.filesExamined.entries()) {
            if (!isRecord(entry)) {
                errors.push(`filesExamined[${index}] must be an object`);
                continue;
            }
            const problem = pathProblem(entry.path, `filesExamined[${index}].path`);
            if (problem) {
                errors.push(problem);
                continue;
            }
            const evidence = entry.evidence;
            if (typeof evidence !== "string" || !evidence.trim()) {
                errors.push(`filesExamined[${index}].evidence must be a non-empty string`);
                continue;
            }
            filesExamined.push({
                path: entry.path.trim(),
                evidence: evidence.slice(0, SCOPING_MEMO_LIMITS.maxEvidenceLength),
            });
        }
    }
    const patterns = readStringList(value.patterns, "patterns", errors, SCOPING_MEMO_LIMITS.maxListEntries, SCOPING_MEMO_LIMITS.maxListEntryLength);
    const risks = readStringList(value.risks, "risks", errors, SCOPING_MEMO_LIMITS.maxListEntries, SCOPING_MEMO_LIMITS.maxListEntryLength);
    const openQuestions = readStringList(value.openQuestions, "openQuestions", errors, SCOPING_MEMO_LIMITS.maxListEntries, SCOPING_MEMO_LIMITS.maxListEntryLength);
    const effortSignal = readEffortSignal(value.effortSignal, errors);
    const validatedCommands = readValidatedCommands(value.validatedCommands, errors);
    let filesToChange = { observed: [], declaredNew: [] };
    if (!isRecord(value.filesToChange)) {
        errors.push("filesToChange must be an object with observed and declaredNew arrays");
    }
    else {
        filesToChange = {
            observed: readPathList(value.filesToChange.observed, "filesToChange.observed", errors),
            declaredNew: readPathList(value.filesToChange.declaredNew, "filesToChange.declaredNew", errors),
        };
    }
    if (errors.length > 0 || !effortSignal) {
        return { ok: false, errors: errors.length > 0 ? errors : ["effortSignal is invalid"] };
    }
    return {
        ok: true,
        draft: {
            filesExamined,
            patterns,
            effortSignal,
            risks,
            filesToChange,
            validatedCommands,
            openQuestions,
        },
    };
}
/** Full Git object id, lowercased. SHA-1 (40) or SHA-256 (64) object ids. */
export function isScopingBaseSha(value) {
    return typeof value === "string" && /^[a-f0-9]{40}$|^[a-f0-9]{64}$/.test(value.trim().toLowerCase());
}
/** Narrow a value read back out of JSONB to a complete memo. */
export function isScopingMemo(value) {
    if (!isRecord(value))
        return false;
    if (value.schemaVersion !== SCOPING_MEMO_SCHEMA_VERSION)
        return false;
    if (!isScopingBaseSha(value.baseSha))
        return false;
    if (typeof value.producedByRunId !== "string" || !value.producedByRunId.trim())
        return false;
    if (typeof value.elapsedMs !== "number" || !Number.isFinite(value.elapsedMs) || value.elapsedMs < 0) {
        return false;
    }
    if (typeof value.costMillicents !== "number"
        || !Number.isFinite(value.costMillicents)
        || value.costMillicents < 0) {
        return false;
    }
    return parseScopingMemoDraft(value).ok;
}
/**
 * The form of a question the cache key is computed over: trimmed, collapsed
 * whitespace, lowercased. Two reviewers who ask the same thing with different
 * spacing or capitalisation get the same answer without a second sandbox, and
 * nothing else about the question is normalized away.
 *
 * Lives in shared, next to the artifact it keys, so the hash is defined in one
 * place even though only the backend computes it (hashing needs node:crypto,
 * which has no business in a browser bundle).
 */
export function normalizeScopingQuestion(question) {
    return question.trim().replace(/\s+/g, " ").toLowerCase();
}
/** A sha256 hex digest, the only shape a stored `questionHash` may take. */
export function isScopingQuestionHash(value) {
    return typeof value === "string" && /^[a-f0-9]{64}$/.test(value);
}
/** Narrow a recorded scoping actor. */
export function isScopingActor(value) {
    if (!isRecord(value))
        return false;
    if (value.kind === "system")
        return true;
    return (value.kind === "user"
        && typeof value.userId === "string"
        && value.userId.trim().length > 0);
}
/** Narrow a `runs.artifacts` element to a persisted memo artifact. */
export function isScopingMemoArtifact(value) {
    if (!isRecord(value))
        return false;
    if (value.kind !== SCOPING_MEMO_ARTIFACT_KIND)
        return false;
    if (typeof value.workItemId !== "string" || !value.workItemId.trim())
        return false;
    // Absent is fine (pre-dates the field); present-but-malformed is not, because
    // a garbled audit note is worse than none.
    if (value.actor !== undefined && !isScopingActor(value.actor))
        return false;
    // Same rule for the cache key, and it matters more: a hash nobody can compare
    // would make this memo answer questions it never asked.
    if (value.questionHash !== undefined && !isScopingQuestionHash(value.questionHash))
        return false;
    if (value.repoId !== undefined && (typeof value.repoId !== "string" || !value.repoId.trim()))
        return false;
    return isScopingMemo(value.memo);
}
/** Bounds on everything copied out of a memo into `source_ref`. */
export const PROPOSAL_SCOPING_REF_LIMITS = {
    maxSampleFiles: 5,
    maxFilesToChange: 20,
    maxRisks: 5,
    maxValidatedCommands: 5,
    maxAskBodyLength: 2_000,
    maxReasonLength: 500,
};
/** Narrow a `source_ref.scoping` value read back out of JSONB. */
export function isProposalScopingRef(value) {
    if (!isRecord(value))
        return false;
    if (value.status !== "pending" && value.status !== "scoped" && value.status !== "unscoped") {
        return false;
    }
    if (!isScopingBaseSha(value.baseSha))
        return false;
    return typeof value.requestedAt === "string" && value.requestedAt.trim() !== "";
}
/**
 * One sentence naming the effort call, for a gate body or a card. Evidence and
 * effort only — never spend, per ADR-0027's no-meter-at-consent rule.
 *
 * Lives here rather than in the backend so the surface that renders the stored
 * {@link ProposalScopingRef} says exactly what the gate body said.
 */
export function describeScopingEffort(effort) {
    const reach = `${effort.blastRadius === "small" ? "Small" : effort.blastRadius === "medium" ? "Medium" : "Large"} blast radius`;
    const build = effort.buildRan ? "the build ran" : "the build did not run";
    const tests = !effort.testsRan
        ? "the tests were not run"
        : effort.testsPassed
            ? "the tests passed"
            : "the tests failed";
    return `${reach}; ${build} and ${tests}.`;
}
//# sourceMappingURL=scoping.js.map